import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, List, BarChart3, Trophy, Settings, Menu, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import CalendarView from "@/components/CalendarView";
import ListView from "@/components/ListView";
import StatsView from "@/components/StatsView";
import EntryModal from "@/components/EntryModal";
import TrophyModal from "@/components/TrophyModal";
import SettingsModal from "@/components/SettingsModal";
import CongratsModal from "@/components/CongratsModal";
import NotificationDropdown from "@/components/NotificationDropdown";
import ReviewModal from "@/components/ReviewModal";
import { toast } from "sonner";
import { apiFetch, clearSessionExpiry, setSessionExpiry } from "@/lib/apiClient";

// Daily quotes
const dailyQuotes = [
  { text: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.", author: "Aristotle" },
  { text: "Motivation is what gets you started. Habit is what keeps you going.", author: "Jim Rohn" },
  { text: "Success is the sum of small efforts repeated day in and day out.", author: "Robert Collier" },
  { text: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
  { text: "Small deeds done are better than great deeds planned.", author: "Peter Marshall" },
  { text: "He who conquers himself is the mightiest warrior.", author: "Confucius" },
  { text: "Self discipline is freedom.", author: "Jocko Willink" },
  { text: "We must all suffer one of two things. The pain of discipline or the pain of regret.", author: "Jim Rohn" },
  { text: "The first and best victory is to conquer self.", author: "Plato" },
  { text: "Without continual growth and progress, such words as improvement and success have no meaning.", author: "Benjamin Franklin" },
  { text: "Energy and persistence conquer all things.", author: "Benjamin Franklin" },
  { text: "It does not matter how slowly you go so long as you do not stop.", author: "Confucius" },
  { text: "Well done is better than well said.", author: "Benjamin Franklin" },
  { text: "Action is the foundational key to all success.", author: "Pablo Picasso" },
  { text: "You miss 100% of the shots you don't take.", author: "Wayne Gretzky" },
  { text: "Perseverance is not a long race. It is many short races one after another.", author: "Walter Elliot" },
  { text: "Do the thing you fear, and the death of fear is certain.", author: "Ralph Waldo Emerson" },
  { text: "Consistency is self respect in motion.", author: null },
  { text: "Small promises kept build unshakeable confidence.", author: null },
  { text: "You don't rise to goals. You sink to habits.", author: null },
  { text: "Discipline is future pride paid for in advance.", author: null },
  { text: "The person you become is hiding in your routines.", author: null },
  { text: "Every habit executed is a quiet vote for a powerful identity.", author: null },
  { text: "Consistency is boring. Results are not.", author: null },
  { text: "What you repeat, you become.", author: null },
  { text: "Self trust is built one kept promise at a time.", author: null },
  { text: "Your future self is watching what you do today.", author: null },
  { text: "Habits turn effort into inevitability.", author: null },
  { text: "Pride comes from follow through.", author: null },
  { text: "Discipline is doing what you said you would do.", author: null },
  { text: "Tiny actions done daily beat heroic efforts done rarely.", author: null },
  { text: "Your standards live in your habits.", author: null },
  { text: "You can't fake consistency.", author: null },
  { text: "Habits are how character shows up quietly.", author: null },
  { text: "Confidence is a side effect of kept commitments.", author: null },
  { text: "What feels small today compounds into a powerful identity.", author: null },
  { text: "Consistency turns potential into evidence.", author: null },
  { text: "You become credible to yourself first.", author: null },
  { text: "Habits are the architecture of self respect.", author: null },
  { text: "Habits make hard things easier by repetition.", author: null },
  { text: "The mirror remembers what motivation forgets.", author: null },
  { text: "Your life is mostly autopilot. Program it well.", author: null },
  { text: "Habits protect you from your moods.", author: null },
  { text: "Discipline is compassion for your future self.", author: null },
  { text: "Daily effort beats occasional brilliance.", author: null },
  { text: "Your routine is your real resume.", author: null },
  { text: "Habits quietly shape your ceiling.", author: null },
  { text: "Pride grows where excuses die.", author: null },
  { text: "The smallest win repeated becomes a lifestyle.", author: null },
  { text: "Consistency builds confidence.", author: null },
  { text: "Consistency outlives motivation every time.", author: null },
  { text: "Habits are quiet. Results make noise.", author: null },
];

const DEFAULT_CATEGORIES = {
  health: { name: "Health & Fitness", color: "#22C55E" },
  career: { name: "Career & Wealth", color: "#3B82F6" },
  family: { name: "Family & Home", color: "#EC4899" },
  relationships: { name: "Relationships & Connection", color: "#F97316" },
  learning: { name: "Learning & Growth", color: "#06B6D4" },
  spirit: { name: "Spirit & Contribution", color: "#A855F7" }
};

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [view, setView] = useState("calendar");
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  
  // Modal states
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [trophyModalOpen, setTrophyModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [congratsModalOpen, setCongratsModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewStreakMilestone, setReviewStreakMilestone] = useState(3);
  const [newTrophies, setNewTrophies] = useState([]);
  const [lastSubmittedEntry, setLastSubmittedEntry] = useState({ text: '', category: '' });
  const [menuOpen, setMenuOpen] = useState(false);

  // Get today's quote based on day of year
  const todayQuote = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    return dailyQuotes[dayOfYear % dailyQuotes.length];
  }, []);

  // Dark mode effect
  useEffect(() => {
    if (user?.settings?.dark_mode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [user?.settings?.dark_mode]);

  // Fetch data
  useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchCategories();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await apiFetch('/entries');
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiFetch('/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const refetchUser = async () => {
    try {
      const response = await apiFetch('/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setSessionExpiry(userData.session_expires_at);
        setUser(userData);
      }
    } catch (error) {
      console.error('Error refetching user:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiFetch('/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        
        // Check if we should show review modal
        checkReviewPrompt(data.current_streak);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const checkReviewPrompt = async (currentStreak) => {
    try {
      const response = await apiFetch('/review/status');
      
      if (response.ok) {
        const status = await response.json();
        
        // Don't show if user has already left a review
        if (status.has_left_review) return;
        
        // Check for 3-day milestone
        if (currentStreak >= 3 && !status.review_prompted_3_day) {
          setReviewStreakMilestone(3);
          setReviewModalOpen(true);
          return;
        }
        
        // Check for 10-day milestone
        if (currentStreak >= 10 && !status.review_prompted_10_day) {
          setReviewStreakMilestone(10);
          setReviewModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error checking review status:', error);
    }
  };

  const handleReviewSubmitted = async () => {
    setReviewModalOpen(false);
    // Mark the milestone as prompted
    await apiFetch(`/review/prompted?milestone=${reviewStreakMilestone}`, {
      method: 'PUT',
    });
  };

  const handleReviewRemindLater = () => {
    setReviewModalOpen(false);
    // Don't mark as prompted, so it will show again next time
  };

  const handleReviewDismiss = async () => {
    setReviewModalOpen(false);
    // Mark the milestone as prompted so it won't show again
    await apiFetch(`/review/prompted?milestone=${reviewStreakMilestone}`, {
      method: 'PUT',
    });
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setEditingEntry(null);
    setEntryModalOpen(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setSelectedDate(entry.date);
    setEntryModalOpen(true);
  };

  const handleSaveEntry = async (entryData) => {
    try {
      let response;
      
      if (editingEntry) {
        response = await apiFetch(`/entries/${editingEntry.entry_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryData)
        });
        
        if (response.ok) {
          toast.success("Win updated!");
          fetchEntries();
          fetchStats();
        }
      } else {
        response = await apiFetch('/entries', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...entryData, date: selectedDate })
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Store the submitted entry for the congrats modal
          setLastSubmittedEntry({ text: entryData.text, category: entryData.category });
          
          // Check for new trophies
          if (data.new_trophies && data.new_trophies.length > 0) {
            setNewTrophies(data.new_trophies);
          }
          
          // Show congrats modal
          setCongratsModalOpen(true);
          
          fetchEntries();
          fetchStats();
        }
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error("Failed to save win");
    }
    
    setEntryModalOpen(false);
    setEditingEntry(null);
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await apiFetch(`/entries/${entryId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        toast.success("Entry deleted");
        fetchEntries();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error("Failed to delete entry");
    }
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', {
        method: 'POST',
      });
      clearSessionExpiry();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSettingsUpdate = async (newSettings) => {
    try {
      const response = await apiFetch('/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      
      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        toast.success("Settings updated");
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error("Failed to update settings");
    }
  };

  // Get dates with entries
  const entryDates = useMemo(() => {
    return new Set(entries.map(e => e.date));
  }, [entries]);

  return (
    <div className="min-h-screen bg-background relative z-10" data-testid="dashboard">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left spacer */}
            <div className="w-10" />
            
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Proud Every Day
            </h1>
            
            {/* Notifications & Menu */}
            <div className="flex items-center gap-1">
              <NotificationDropdown />
              
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMenuOpen(!menuOpen)}
                  data-testid="menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
                
                {menuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setMenuOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-xl shadow-lg z-50 py-2">
                      <button
                        onClick={() => { setTrophyModalOpen(true); setMenuOpen(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                        data-testid="menu-trophies"
                      >
                        <Trophy className="w-4 h-4" />
                        Trophy Case
                      </button>
                      <button
                        onClick={() => { setSettingsModalOpen(true); setMenuOpen(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                        data-testid="menu-settings"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Quote of the Day */}
      <div className="daily-quote border-b border-border">
        <blockquote>"{todayQuote.text}"</blockquote>
        {todayQuote.author && <cite>— {todayQuote.author}</cite>}
        <div className="mt-4">
          <Button
            onClick={() => {
              const today = new Date();
              const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              setSelectedDate(todayStr);
              setEditingEntry(null);
              setEntryModalOpen(true);
            }}
            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-6"
            data-testid="submit-win-btn"
          >
            Submit Proud Moment
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="border-b border-border bg-card/50 py-4">
          <div className="max-w-lg mx-auto flex items-start justify-between px-4">
            {/* Proud Days */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1">
                <Calendar className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold tabular-nums">{entryDates.size}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Proud Days</p>
            </div>
            
            {/* Proud Moments */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1">
                <Trophy className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold tabular-nums">{stats.total_entries}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Proud Moments</p>
            </div>
            
            {/* Streak */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1">
                <Flame className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold tabular-nums">{stats.current_streak}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Streak</p>
            </div>
            
            {/* Consistency */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1">
                <BarChart3 className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold tabular-nums">{stats.consistency_score || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Consistency</p>
            </div>
          </div>
        </div>
      )}

      {/* View Toggles */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-center">
          <div className="view-toggles">
            <button
              onClick={() => setView("calendar")}
              className={`view-toggle-btn ${view === "calendar" ? "active" : ""}`}
              data-testid="view-calendar"
            >
              <Calendar className="w-5 h-5" />
            </button>
            <button
              onClick={() => setView("list")}
              className={`view-toggle-btn ${view === "list" ? "active" : ""}`}
              data-testid="view-list"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setView("stats")}
              className={`view-toggle-btn ${view === "stats" ? "active" : ""}`}
              data-testid="view-stats"
            >
              <BarChart3 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {view === "calendar" && (
              <>
                <CalendarView 
                  entryDates={entryDates}
                  onDayClick={handleDayClick}
                  weekStartsMonday={user?.settings?.week_starts_monday !== false}
                />
                
                {/* Latest Proud Moments */}
                {entries.length > 0 && (
                  <div className="mt-8 max-w-md mx-auto">
                    <h3 className="text-lg font-heading font-semibold mb-4">Latest Proud Moments</h3>
                    <div className="space-y-3">
                      {entries
                        .sort((a, b) => b.date.localeCompare(a.date) || b.created_at?.localeCompare(a.created_at))
                        .slice(0, 5)
                        .map(entry => {
                          const category = categories[entry.category];
                          const date = new Date(entry.date);
                          const formattedDate = date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                          });
                          return (
                            <div 
                              key={entry.entry_id}
                              className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border cursor-pointer hover:border-primary/30 transition-colors"
                              onClick={() => handleEditEntry(entry)}
                            >
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: category?.color || '#888' }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">{entry.text}</p>
                              </div>
                              <div className="text-xs text-muted-foreground flex-shrink-0">
                                {formattedDate}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </>
            )}
            {view === "list" && (
              <ListView 
                entries={entries}
                categories={categories}
                onEditEntry={handleEditEntry}
                onDeleteEntry={handleDeleteEntry}
              />
            )}
            {view === "stats" && stats && (
              <StatsView 
                stats={stats}
                categories={categories}
                weekStartsMonday={user?.settings?.week_starts_monday !== false}
              />
            )}
          </>
        )}
      </main>

      {/* Modals */}
      <EntryModal
        open={entryModalOpen}
        onOpenChange={setEntryModalOpen}
        selectedDate={selectedDate}
        editingEntry={editingEntry}
        categories={categories}
        entries={entries.filter(e => e.date === selectedDate)}
        onSave={handleSaveEntry}
        onDelete={handleDeleteEntry}
      />

      <TrophyModal
        open={trophyModalOpen}
        onOpenChange={setTrophyModalOpen}
      />

      <SettingsModal
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
        user={user}
        onSettingsUpdate={handleSettingsUpdate}
        onLogout={handleLogout}
        categories={categories}
        onCategoriesUpdate={fetchCategories}
        onUserUpdate={refetchUser}
      />

      <CongratsModal
        open={congratsModalOpen}
        onOpenChange={setCongratsModalOpen}
        newTrophies={newTrophies}
        proudMoment={lastSubmittedEntry.text}
        category={categories[lastSubmittedEntry.category]?.name || lastSubmittedEntry.category}
        onClose={() => {
          setCongratsModalOpen(false);
          setNewTrophies([]);
          setLastSubmittedEntry({ text: '', category: '' });
        }}
      />

      <ReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        streakDays={reviewStreakMilestone}
        onReviewSubmitted={handleReviewSubmitted}
        onRemindLater={handleReviewRemindLater}
        onDismiss={handleReviewDismiss}
      />
    </div>
  );
};

export default Dashboard;
